import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router-dom";
import {useSelector} from "react-redux";
import {Button} from "primereact/button";
import {Column} from "primereact/column";
import {DataTable} from "primereact/datatable";
import {Dropdown} from "primereact/dropdown";
import {InputText} from "primereact/inputtext";
import {Tag} from "primereact/tag";
import PageHeaderButton from "../components/page-header-button";
import AdminAccessDenied from "../../system/components/admin-access-denied";
import {ADMIN_PERMISSIONS, hasAdminPermission} from "../../system/admin-access";
import {fetchAllAdminCollection} from "../../system/content-type-admin-api";
import {CONTENT_TYPE_UIDS} from "../../system/content-type-registry";
import {useTranslate} from "../../system/useTranslate";
import {parseDate} from "../../system/utils";
import {entityId, groupBy} from "../operations/operations-utils";

function errorText(error) {
    return error?.response?.data?.error?.message || error?.message || "users-load-error";
}

function userIdOfMembership(membership) {
    return entityId(membership?.member);
}

function membershipIdOfPayment(payment) {
    return entityId(payment?.user);
}

function OperationsStat({label, value, severity}) {
    const translate = useTranslate();
    return (
        <div className="col-12 md:col-6 xl:col-3">
            <div className="surface-card border-1 border-200 border-round p-3 flex align-items-center justify-content-between gap-3 h-full">
                <span className="text-sm text-600">{translate(label)}</span>
                <Tag value={value} severity={severity}/>
            </div>
        </div>
    );
}

function Index() {
    const navigate = useNavigate();
    const translate = useTranslate();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [query, setQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState(null);
    const adminUser = useSelector((state) => state.auth.user);

    const loadUsers = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const requests = await Promise.allSettled([
                fetchAllAdminCollection(CONTENT_TYPE_UIDS.USER, {pageSize: 200}),
                fetchAllAdminCollection(CONTENT_TYPE_UIDS.WORKSPACE_MEMBERSHIP, {pageSize: 200}),
                fetchAllAdminCollection(CONTENT_TYPE_UIDS.USER_EVENT_LOG, {pageSize: 200}),
                fetchAllAdminCollection(CONTENT_TYPE_UIDS.PAYMENT, {pageSize: 200}),
            ]);
            const items = (index) => requests[index].status === "fulfilled"
                ? requests[index].value.items
                : [];
            if (requests[0].status === "rejected") throw requests[0].reason;

            const memberships = items(1);
            const events = items(2);
            const payments = items(3);
            const membershipsByUser = groupBy(memberships, userIdOfMembership);
            const eventsByUser = groupBy(events, (event) => entityId(event.owner));
            const membershipById = new Map(
                memberships.map((membership) => [String(entityId(membership)), membership])
            );
            const failedPaymentsByUser = new Map();
            payments
                .filter((payment) => ["failed", "canceled"].includes(payment.paymentStatus))
                .forEach((payment) => {
                    const membership = membershipById.get(String(membershipIdOfPayment(payment)));
                    const userId = userIdOfMembership(membership);
                    if (userId == null) return;
                    const key = String(userId);
                    failedPaymentsByUser.set(key, (failedPaymentsByUser.get(key) || 0) + 1);
                });

            setRows(items(0).map((user) => {
                const userId = String(entityId(user));
                const userMemberships = membershipsByUser.get(userId) || [];
                const userEvents = (eventsByUser.get(userId) || []).sort(
                    (left, right) => new Date(right.createdAt) - new Date(left.createdAt)
                );
                const status = user.blocked ? "blocked" : !user.confirmed ? "unconfirmed" : "active";
                return {
                    ...user,
                    _key: entityId(user),
                    _name: user.fullname || [user.firstname, user.lastname].filter(Boolean).join(" ")
                        || user.username || "-",
                    _status: status,
                    _memberships: userMemberships.length,
                    _joinedMemberships: userMemberships.filter(
                        (membership) => membership.memberShipStatus === "joined"
                    ).length,
                    _failedPayments: failedPaymentsByUser.get(userId) || 0,
                    _lastActivity: userEvents[0]?.createdAt || user.updatedAt || user.createdAt,
                };
            }));
        } catch (requestError) {
            setError(errorText(requestError));
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, []);

    const canViewUsers = hasAdminPermission(adminUser, ADMIN_PERMISSIONS.USER_VIEW);

    useEffect(() => {
        if (canViewUsers) loadUsers();
    }, [canViewUsers, loadUsers]);

    const visibleRows = useMemo(() => {
        const normalizedQuery = query.trim().toLowerCase();
        return rows.filter((row) => {
            if (statusFilter && row._status !== statusFilter) return false;
            if (!normalizedQuery) return true;
            return [row._name, row.email, row.phonenumber, row.username]
                .some((value) => String(value || "").toLowerCase().includes(normalizedQuery));
        });
    }, [query, rows, statusFilter]);

    const blockedCount = rows.filter((row) => row._status === "blocked").length;
    const unconfirmedCount = rows.filter((row) => row._status === "unconfirmed").length;
    const withoutWorkspaceCount = rows.filter((row) => row._joinedMemberships === 0).length;

    if (!canViewUsers) {
        return <AdminAccessDenied/>;
    }

    return (
        <div className="w-full h-full overflow-hidden flex flex-column gap-3 p-3">
            <PageHeaderButton
                title="users"
                description={translate("users-operations-description")}
                end={(
                    <Button
                        icon="pi pi-refresh"
                        label={translate("refresh")}
                        className="p-button-sm p-button-outlined gap-2"
                        loading={loading}
                        onClick={loadUsers}
                    />
                )}
            />

            {error && <div className="text-red-500 text-sm px-3">{translate(error)}</div>}

            <div className="grid m-0">
                <OperationsStat label={translate("total-users")} value={rows.length} severity="info"/>
                <OperationsStat label={translate("blocked-users")} value={blockedCount} severity="danger"/>
                <OperationsStat label={translate("unconfirmed-users")} value={unconfirmedCount} severity="warning"/>
                <OperationsStat label={translate("users-without-workspace")} value={withoutWorkspaceCount} severity="warning"/>
            </div>

            <div className="surface-card border-1 border-200 border-round p-3 flex flex-wrap align-items-center gap-2">
                <span className="p-input-icon-right flex-1 min-w-16rem">
                    <i className="pi pi-search"/>
                    <InputText
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        placeholder={translate("user-search-placeholder")}
                        className="w-full"
                    />
                </span>
                <Dropdown
                    value={statusFilter}
                    onChange={(event) => setStatusFilter(event.value)}
                    options={["active", "blocked", "unconfirmed"].map((value) => ({
                        value,
                        label: translate(`user-status-${value}`),
                    }))}
                    placeholder={translate("user-status-filter")}
                    showClear
                    className="min-w-12rem"
                />
            </div>

            <div className="surface-card border-1 border-200 border-round flex-1 overflow-hidden">
                <DataTable
                    value={visibleRows}
                    dataKey="_key"
                    loading={loading}
                    scrollable
                    scrollHeight="flex"
                    rowHover
                    emptyMessage={translate("no-records")}
                    onRowClick={(event) => navigate(`/users/${event.data.documentId || event.data.id}`)}
                    rowClassName="cursor-pointer"
                    className="h-full"
                    tableClassName="text-sm"
                >
                    <Column
                        header={translate("user")}
                        body={(row) => (
                            <div className="flex flex-column gap-1">
                                <span className="font-medium text-900">{row._name}</span>
                                <span className="text-xs text-500 ltr">{row.email || "-"}</span>
                            </div>
                        )}
                        bodyClassName="text-right"
                        headerClassName="text-right"
                    />
                    <Column field="phonenumber" header={translate("phone-number")} bodyClassName="text-right ltr" headerClassName="text-right"/>
                    <Column
                        header={translate("status")}
                        body={(row) => (
                            <Tag
                                value={translate(`user-status-${row._status}`)}
                                severity={row._status === "active" ? "success" : row._status === "blocked" ? "danger" : "warning"}
                            />
                        )}
                        bodyClassName="text-right"
                        headerClassName="text-right"
                    />
                    <Column
                        header={translate("user-workspaces")}
                        body={(row) => (
                            <span className="ltr">{row._joinedMemberships} / {row._memberships}</span>
                        )}
                        bodyClassName="text-right"
                        headerClassName="text-right"
                    />
                    <Column
                        field="_failedPayments"
                        header={translate("failed-payments")}
                        body={(row) => (
                            <Tag
                                value={row._failedPayments}
                                severity={row._failedPayments > 0 ? "danger" : "success"}
                            />
                        )}
                        bodyClassName="text-right"
                        headerClassName="text-right"
                    />
                    <Column
                        header={translate("last-activity")}
                        body={(row) => row._lastActivity
                            ? parseDate(row._lastActivity, "iso", "YYYY/MM/DD HH:mm", "gregorian", "en")
                            : "-"}
                        bodyClassName="text-right ltr"
                        headerClassName="text-right"
                    />
                    <Column
                        header={translate("security")}
                        body={(row) => (
                            <div className="flex gap-1 flex-wrap">
                                <Tag
                                    value={translate(row.confirmed ? "confirmed" : "not-confirmed")}
                                    severity={row.confirmed ? "success" : "warning"}
                                />
                                <Tag
                                    value={translate(row.verified ? "verified" : "not-verified")}
                                    severity={row.verified ? "success" : "warning"}
                                />
                            </div>
                        )}
                        bodyClassName="text-right"
                        headerClassName="text-right"
                    />
                    <Column
                        header={translate("actions")}
                        body={(row) => (
                            <Button
                                icon="pi pi-arrow-left"
                                label={translate("user-open-operations")}
                                className="p-button-sm p-button-text gap-2"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    navigate(`/users/${row.documentId || row.id}`);
                                }}
                            />
                        )}
                        bodyClassName="text-right"
                        headerClassName="text-right"
                    />
                </DataTable>
            </div>
        </div>
    );
}

export default Index;
